import { useState, useEffect, useMemo, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type RowSelectionState,
  flexRender,
} from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const INTERACTIVE_DESCENDANT_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "[contenteditable='true']",
  "[role='button']",
  "[role='link']",
  "[role='checkbox']",
  "[role='menuitem']",
  "[tabindex]",
].join(",");

function accessibleHeaderName(header: unknown, columnId: string): string {
  if (typeof header === "string" && header.trim()) return header.trim();
  return columnId
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

function cameFromInteractiveDescendant(target: EventTarget | null, row: HTMLTableRowElement): boolean {
  if (!(target instanceof Element)) return false;
  const interactive = target.closest(INTERACTIVE_DESCENDANT_SELECTOR);
  return interactive !== null && interactive !== row && row.contains(interactive);
}

interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  searchColumn?: string;
  searchColumns?: string[];
  countLabel?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  initialSorting?: SortingState;
  enableRowSelection?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  getRowId?: (row: T) => string;
}

export function DataTable<T>({
  columns,
  data,
  searchPlaceholder = "Search...",
  onRowClick,
  searchColumn,
  searchColumns,
  countLabel = "result",
  pageSize = 25,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  initialSorting,
  enableRowSelection = false,
  onSelectionChange,
  getRowId,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Reset to page 0 when search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [globalFilter]);

  // Clear row selection when data reference changes (e.g. after filter change)
  // Skip the initial mount to avoid unnecessary cascading re-renders
  const prevDataRef = useRef(data);
  useEffect(() => {
    if (prevDataRef.current !== data) {
      setRowSelection({});
      prevDataRef.current = data;
    }
  }, [data]);

  // Build columns with optional checkbox column prepended
  const allColumns = useMemo(() => {
    if (!enableRowSelection) return columns;

    const checkboxColumn: ColumnDef<T, unknown> = {
      id: "_select",
      header: ({ table }) => (
        <div
          className="flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={table.getIsAllPageRowsSelected() ? "Deselect all rows on this page" : "Select all rows on this page"}
            className="h-4 w-4 rounded border-border text-teal focus:ring-teal"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div
          className="flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={row.getIsSelected() ? `Deselect row ${row.index + 1}` : `Select row ${row.index + 1}`}
            className="h-4 w-4 rounded border-border text-teal focus:ring-teal"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
      enableSorting: false,
    };

    return [checkboxColumn, ...columns];
  }, [columns, enableRowSelection]);

  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      sorting,
      globalFilter,
      pagination,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    ...(enableRowSelection
      ? {
          enableRowSelection: true,
          onRowSelectionChange: setRowSelection,
          getRowId,
        }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: searchColumns
      ? (row, _columnId, filterValue) => {
          const filter = String(filterValue).toLowerCase();
          return searchColumns.some((col) => {
            const value = row.getValue(col);
            return String(value ?? "").toLowerCase().includes(filter);
          });
        }
      : searchColumn
        ? (row, _columnId, filterValue) => {
            const value = row.getValue(searchColumn);
            return String(value ?? "")
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          }
        : "includesString",
  });

  // Notify parent of selection changes — skip redundant empty→empty notifications
  const prevSelectionCountRef = useRef(0);
  useEffect(() => {
    if (!enableRowSelection || !onSelectionChange) return;

    const selectedRows = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);

    // Avoid notifying parent with empty arrays when selection was already empty
    if (selectedRows.length === 0 && prevSelectionCountRef.current === 0) return;
    prevSelectionCountRef.current = selectedRows.length;

    onSelectionChange(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, enableRowSelection]);

  const totalFiltered = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const visibleColumnCount = allColumns.length;

  return (
    <div>
      <input
        type="text"
        aria-label={searchPlaceholder}
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-4 w-full max-w-sm rounded-md border border-border bg-white px-3 py-2 font-body text-sm text-text placeholder-text-muted focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
      />

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-left">
          <thead className="bg-sidebar">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        canSort
                          ? sorted === "asc"
                            ? "ascending"
                            : sorted === "desc"
                              ? "descending"
                              : "none"
                          : undefined
                      }
                      className={cn(
                        "px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-text-muted",
                        header.id === "_select" && "w-10"
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          aria-label={`Sort by ${accessibleHeaderName(header.column.columnDef.header, header.column.id)}`}
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex w-full select-none items-center gap-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
                        >
                          {content}
                          <span aria-hidden="true" className="text-text-muted/60">
                            {sorted === "asc" ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : sorted === "desc" ? (
                              <ArrowDown className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpDown className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-1">{content}</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumnCount}
                  className="px-4 py-8 text-center font-body text-sm text-text-muted"
                >
                  No results found.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={(event) => {
                    if (!onRowClick || cameFromInteractiveDescendant(event.target, event.currentTarget)) return;
                    onRowClick(row.original);
                  }}
                  onKeyDown={(event) => {
                    if (!onRowClick || event.target !== event.currentTarget || event.repeat) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onRowClick(row.original);
                    }
                  }}
                  className={cn(
                    "transition-colors",
                    onRowClick && "cursor-pointer hover:bg-sidebar/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal",
                    enableRowSelection &&
                      row.getIsSelected() &&
                      "bg-teal/5"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-4 py-3 font-body text-sm text-text",
                        cell.column.id === "_select" && "w-10"
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <p className="font-body text-xs text-text-muted">
          {totalFiltered} {countLabel}
          {totalFiltered !== 1 ? "s" : ""}
        </p>

        {totalFiltered > 0 && (
          <div className="flex items-center gap-3">
            <select
              aria-label="Rows per page"
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })
              }
              className="rounded-md border border-border bg-white px-2 py-1 font-body text-xs text-text focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>

            {pageCount > 1 && (
              <>
                <span className="font-body text-xs text-text-muted">
                  Page {table.getState().pagination.pageIndex + 1} of{" "}
                  {pageCount}
                </span>

                <div className="flex gap-1">
                  <button
                    type="button"
                    aria-label="Previous page"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="rounded-md border border-border bg-white p-1 text-text-muted transition-colors hover:bg-sidebar disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next page"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="rounded-md border border-border bg-white p-1 text-text-muted transition-colors hover:bg-sidebar disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
